import { Disclosure } from "@headlessui/react";
import { IconFileExport, IconFilter } from "@tabler/icons-react";
import classNames from "classnames";
import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { __ } from "@common/helpers/i18n";
import { SidebarProps } from "@common/types";
import { useDocumentDirection } from "@app/hooks";
import { Button } from "../form";
import { FadeTransition } from "../helpers";
import { Section } from "./Section";

export function Sidebar({
  filter,
  children,
  search,
  hasActiveItem,
  actionButton,
}: SidebarProps) {
  const root = useMemo(() => document.getElementById("start-menu"), []);
  const direction = useDocumentDirection();

  return (
    <>
      {createPortal(
        <div
          data-admin-side-panel="true"
          className={classNames(
            "admin-side-panel flex h-full w-screen flex-shrink-0 border-border bg-card p-0 md:w-[270px] md:overflow-hidden xl:order-first",
            direction === "rtl" ? "border-l" : "border-r",
            hasActiveItem ? "hidden md:flex" : "",
          )}
        >
          <Section
            pure
            className={classNames(
              "relative flex h-full w-full flex-shrink-0 flex-col md:overflow-hidden xl:order-first",
              hasActiveItem ? "hidden md:flex" : "",
            )}
          >
            {(search || filter) && (
              <div className="relative border-b border-border bg-card px-3 py-3">
                {filter ? (
                  <Disclosure>
                    {({ open }) => (
                      <>
                        <div className="flex items-center gap-2">
                          <div className="flex-1">{search}</div>
                          <Disclosure.Button as={React.Fragment}>
                            <Button
                              className={
                                "h-11 w-11 rounded-md border-border bg-card px-0 py-0 text-muted-foreground shadow-soft focus:outline-none hover:text-primary" +
                                (open ? " text-primary" : "")
                              }
                              variant="outline-light"
                              padding="none"
                              aria-label={__("Show filters")}
                            >
                              <IconFilter size={20} stroke={2} />
                            </Button>
                          </Disclosure.Button>
                          {actionButton &&
                            React.cloneElement(actionButton, {
                              className: classNames(
                                actionButton.props.className,
                                "h-11 w-11 rounded-md px-0",
                              ),
                              children: <IconFileExport size={18} stroke={2} />,
                              "aria-label": __("Export"),
                            })}
                        </div>
                        <FadeTransition>
                          <Disclosure.Panel className="relative z-10 mt-2 grid gap-2 rounded-md border border-border bg-card p-2 shadow-soft">
                            {filter}
                          </Disclosure.Panel>
                        </FadeTransition>
                      </>
                    )}
                  </Disclosure>
                ) : (
                  search && (
                    <div className="flex items-center gap-2">{search}</div>
                  )
                )}
              </div>
            )}
            {children}
          </Section>
        </div>,
        root!,
      )}
    </>
  );
}
